/**
 * Loads Google Tag Manager and the Google Analytics tag (gtag.js), in production builds only (GROWTH-8098).
 *
 * public/index.html used to load both on every host, so local stacks, pr-local slots, Playwright runs and staging all
 * reported into the production GA4 property (337972812). Each tag now loads only when the build names it:
 * `REACT_APP_GTM_ID` and `REACT_APP_GA_MEASUREMENT_ID` are exported by the `_DEPLOY_PRODUCTION` pipeline and by no
 * other build. Without them `helpers/track.js` still works: `trackDataLayer` creates `window.dataLayer` and nothing
 * reads it.
 *
 * The Meta Pixel stays in public/index.html behind `REACT_APP_FB_PIXEL_ENABLED`; see
 * docs/testing/inventories/analyticsTags.md.
 */

import { applyStoredGaTrafficType } from "./gaTrafficType";

const GTM_ID_PATTERN = /^GTM-[A-Z0-9]+$/;
const GA_MEASUREMENT_ID_PATTERN = /^G-[A-Z0-9]+$/;

let loaded = false;

const appendScript = src => {
  const script = document.createElement("script");
  script.async = true;
  script.src = src;
  document.head.appendChild(script);
};

/**
 * Once per page session: later calls do nothing. An id that is unset or not of its tag's form (an empty value, or a
 * build that left a placeholder) loads nothing for that tag.
 */
export const loadAnalyticsTags = () => {
  if (loaded) {
    return;
  }
  loaded = true;

  const gtmId = process.env.REACT_APP_GTM_ID;
  const gaMeasurementId = process.env.REACT_APP_GA_MEASUREMENT_ID;
  const loadGtag = GA_MEASUREMENT_ID_PATTERN.test(gaMeasurementId || "");
  const loadGtm = GTM_ID_PATTERN.test(gtmId || "");

  if (!loadGtag && !loadGtm) {
    return;
  }

  window.dataLayer = window.dataLayer || [];

  if (loadGtag) {
    appendScript(`https://www.googletagmanager.com/gtag/js?id=${gaMeasurementId}`);
    // gtag.js reads only Arguments objects off dataLayer, so this keeps Google's snippet as it is.
    window.gtag = function gtag() {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer.push(arguments);
    };
    window.gtag("js", new Date());
    // GROWTH-8134: before config, so the page_view that starts the session carries a staff account's mark too.
    applyStoredGaTrafficType();
    window.gtag("config", gaMeasurementId);
  }

  if (loadGtm) {
    window.dataLayer.push({ "gtm.start": new Date().getTime(), event: "gtm.js" });
    appendScript(`https://www.googletagmanager.com/gtm.js?id=${gtmId}`);
  }
};
